//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as yup from 'yup'
import { InferType } from 'yup'
import { ERC20Wei } from './conversions'
import { yupErc20Wei, yupErc20WeiSigned } from './Enums'

import { StringTypeEnum, toFieldMetaData } from './SchemaTypes'
import { convertValuesToDecStr, convertYupSchemaValuesToDecStr } from './schemaUtils'
import {
  PoolBaseAccountCTO,
  PoolBaseAccountCTOSchemaValues,
  PoolBaseAccountSummaryCTOSchemaValues,
} from './PoolBaseAccountCTO'

export const PoolDynamicAccountOnlyCTOSchemaValues: Record<string, yup.Schema> = {
  maxWithdrawRequest: yupErc20Wei.label('Max Withdrawal Request').meta(
    toFieldMetaData({
      propName: 'maxWithdrawRequest',
      description: 'The maximum amount of assets you can request to be withdrawn.',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  maxRedeemRequest: yupErc20Wei.label('Max Tokens Redeemable').meta(
    toFieldMetaData({
      propName: 'maxRedeemRequest',
      description:
        'The maximum amount that can be requested for withdrawal by the account, denominated in Vault Tokens. ',
      stringType: StringTypeEnum.PPT_SHARES,
    })
  ),

  requestedSharesOf: yupErc20Wei.label('Outstanding Withdrawal Requests (Tokens)').meta(
    toFieldMetaData({
      propName: 'requestedSharesOf',
      description: 'The amount of outstanding withdrawal requests for the account, denominated in Vault Tokens. ',
      stringType: StringTypeEnum.PPT_SHARES,
    })
  ),
  requestedAssetsOf: yupErc20Wei.label('Outstanding Withdrawal Requests (Assets)').meta(
    toFieldMetaData({
      propName: 'requestedAssetsOf',
      description: 'The amount of withdrawal request currently pending repayment.',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  acceptedShares: yupErc20Wei.label('Outstanding Withdrawal Accepted (Tokens)').meta(
    toFieldMetaData({
      propName: 'acceptedShares',
      description: 'The amount of outstanding withdrawal requests for the account, denominated in Vault Tokens. ',
      stringType: StringTypeEnum.PPT_SHARES,
    })
  ),
  acceptedAssets: yupErc20Wei.label('Outstanding Withdrawal Accepted (Assets)').meta(
    toFieldMetaData({
      propName: 'acceptedAssets',
      description: 'The amount of withdrawal requests that have been accepted but not yet repaid.',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),

  principalEarningInterest: yupErc20Wei.label('Principal Earning Interest').meta(
    toFieldMetaData({
      propName: 'principalEarningInterest',
      description: 'Your current amount of loan principal earning the Loan Fee Rate.',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),

  assetsDeposited: yupErc20Wei.label('Assets Loaned').meta(
    toFieldMetaData({
      propName: 'assetsDeposited',
      description: 'The amount of assets you have loaned to date.',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  assetsWithdrawn: yupErc20Wei.label('Assets Withdrawn').meta(
    toFieldMetaData({
      propName: 'assetsWithdrawn',
      description: 'The amount of your loans that have been repaid to date.',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),

  gainLoss: yupErc20WeiSigned.label('Gain / Loss').meta(
    toFieldMetaData({
      propName: 'gainLoss',
      description: 'The total gain, loss, or interest accrued for the account to date. Includes debits and credits.',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),

  gainLossInDay: yupErc20WeiSigned.label('Daily Gain / Loss').meta(
    toFieldMetaData({
      propName: 'gainLossInDay',
      description:
        'The total gain, loss, or interest accrued for the account for the day. Includes debits and credits.  ',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),

  debits: yupErc20Wei.label('Debits').meta(
    toFieldMetaData({
      propName: 'debits',
      description: 'The total amount of debits for the account to date. Tokens are burned for debits.  ',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  debitsInDay: yupErc20Wei.label('Daily Debits').meta(
    toFieldMetaData({
      propName: 'debitsInDay',
      description: 'The amount of debits for the account for the day. ',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  credits: yupErc20Wei.label('Credits').meta(
    toFieldMetaData({
      propName: 'credits',
      description: 'The total amount of credits for the account to date. Tokens are minted for credits.  ',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  creditsInDay: yupErc20Wei.label('Daily Credits').meta(
    toFieldMetaData({
      propName: 'creditsInDay',
      description: 'The amount of credits for the account for the day.  ',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  fees: yupErc20Wei.label('Fees').meta(
    toFieldMetaData({
      propName: 'fees',
      description: ' The total amount of transaction fees paid by the account to date. ',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  feesInDay: yupErc20Wei.label('Daily Fees').meta(
    toFieldMetaData({
      propName: 'feesInDay',
      description: ' The amount of fees paid for the day',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
}

export const PoolDynamicAccountSummaryCTOSchema = yup
  .object({
    ...PoolBaseAccountSummaryCTOSchemaValues,
  })
  .label('PoolDynamicAccountSummaryCTO')
  .meta(
    toFieldMetaData({
      propName: 'PoolDynamicAccountSummaryCTO',
      map: 'PoolDynamicAccountCTO',
      description: 'Pool Dynamic Account CTO',
    })
  )
export const PoolDynamicAccountCTOSchema = yup
  .object({
    ...PoolBaseAccountCTOSchemaValues,
    ...PoolDynamicAccountOnlyCTOSchemaValues,
  })
  .label('PoolDynamicAccountCTO')
  .meta(toFieldMetaData({ propName: 'PoolDynamicAccountCTO', description: 'Pool Account CTO' }))

export const PoolDynamicAccountOnlyCTOSchema = yup.object({
  ...PoolDynamicAccountOnlyCTOSchemaValues,
})

export interface PoolDynamicAccountSummaryCTO extends InferType<typeof PoolDynamicAccountSummaryCTOSchema> {}

export interface PoolDynamicAccountCTO extends PoolBaseAccountCTO {
  maxWithdrawRequest: ERC20Wei
  maxRedeemRequest: ERC20Wei
  requestedAssetsOf: ERC20Wei
  requestedSharesOf: ERC20Wei
  assetBalance: ERC20Wei
  acceptedShares: ERC20Wei
  acceptedAssets: ERC20Wei
  assetsDeposited: ERC20Wei
  assetsWithdrawn: ERC20Wei

  gainLoss: ERC20Wei
  gainLossInDay: ERC20Wei
  debits: ERC20Wei
  debitsInDay: ERC20Wei
  credits: ERC20Wei
  creditsInDay: ERC20Wei
  fees: ERC20Wei
  feesInDay: ERC20Wei
}

export const PoolDynamicAccountTTOOnlySchema = yup
  .object({
    liquidityTokenSymbol: yup
      .string()
      .label('liquidityTokenSymbol')
      .meta(toFieldMetaData({ propName: 'liquidityTokenSymbol', stringType: StringTypeEnum.SYMBOL })),
  })
  .concat(yup.object(convertYupSchemaValuesToDecStr(PoolDynamicAccountCTOSchema)))
  .concat(PoolDynamicAccountCTOSchema)

export function PoolLenderReportingAccountDynamicCTOSchema(properties?: string[]): yup.ObjectSchema<any> {
  if (!properties) {
    return yup.object({
      ...PoolBaseAccountCTOSchemaValues,
      ...PoolDynamicAccountOnlyCTOSchemaValues,
    })
  } else {
    const schemaValues: Record<string, yup.Schema> = {}
    properties.forEach((prop) => {
      if (PoolDynamicAccountOnlyCTOSchemaValues[prop]) {
        schemaValues[prop] = PoolDynamicAccountOnlyCTOSchemaValues[prop]
      }
      if (PoolBaseAccountCTOSchemaValues[prop]) {
        schemaValues[prop] = PoolBaseAccountCTOSchemaValues[prop]
      }
      // if (PoolDynamicAccountOnlyCTOSchemaValues[prop]) {
      //   schemaValues[prop] = PoolDynamicAccountOnlyCTOSchemaValues[prop]
      // }
      // if (PoolAccountOnlyCTOSchemaValues[prop]) {
      //   schemaValues[prop] = PoolAccountOnlyCTOSchemaValues[prop]
      // }
      if (!schemaValues[prop]) {
        throw new Error(`Property ${prop} not found in PoolReportingAccountCTOSchema`)
      }
    })

    return yup.object(schemaValues)
  }
}

export interface PoolDynamicAccountTTO
  extends PoolDynamicAccountCTO,
    InferType<typeof PoolDynamicAccountTTOOnlySchema> {}

export function toPoolDynamicAccountTTO(poolDynamicAccountCTO: PoolDynamicAccountCTO): PoolDynamicAccountTTO {
  return {
    ...poolDynamicAccountCTO,

    ...convertValuesToDecStr(PoolDynamicAccountCTOSchema, poolDynamicAccountCTO),
  }
}
